import { gql, TypedDocumentNode } from '@apollo/client';

export const GET_SITES = gql`
  query GetSites(
    $filter: SiteFilter
    $paging: CursorPaging
    $sorting: [SiteSort!]
  ) {
    sites(filter: $filter, paging: $paging, sorting: $sorting) {
      edges {
        node {
          id
          name
          description
        }
      }
    }
  }
`;

export const GET_WHOLESALE_TRADES = gql`
  query GetWholesaleTrades(
    $filter: WholesaleTradeFilter
    $paging: CursorPaging
  ) {
    wholesaleTrades(filter: $filter, paging: $paging) {
      edges {
        node {
          id
          start
          end
          power
          market
          side
          price
          tradeGroupSourceId
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

export const GET_WHOLESALE_TRADES_DOWNLOAD = gql`
  query GetWholesaleTrades(
    $filter: WholesaleTradeFilter
    $paging: CursorPaging
    $siteId: ID!
  ) {
    site(id: $siteId) {
      wholesaleTrades(filter: $filter, paging: $paging) {
        edges {
          node {
            id
            start
            end
            power
            market
            side
            price
            tradeGroupSourceId
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
`;

export const GET_ANCILLARY_TRADES = gql`
  query GetAncillaryTrades(
    $filter: AncillaryTradeFilter
    $paging: CursorPaging
  ) {
    ancillaryTrades(filter: $filter, paging: $paging) {
      edges {
        node {
          id
          start
          end
          power
          market
          side
          price
          tradeGroupSourceId
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

export const GET_ANCILLARY_TRADES_DOWNLOAD = gql`
  query GetAncillaryTrades(
    $filter: AncillaryTradeFilter
    $paging: CursorPaging
    $siteId: ID!
  ) {
    site(id: $siteId) {
      ancillaryTrades(filter: $filter, paging: $paging) {
        edges {
          node {
            id
            start
            end
            power
            market
            side
            price
            tradeGroupSourceId
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
`;

export const GET_BALANCING_MECHANISM_TRADES = gql`
  query GetBalancingMechanismTrades(
    $filter: BalancingMechanismTradeFilter
    $paging: CursorPaging
  ) {
    balancingMechanismTrades(filter: $filter, paging: $paging) {
      edges {
        node {
          id
          start
          end
          power
          market
          side
          price
          tradeGroupSourceId
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

export const GET_BALANCING_MECHANISM_TRADES_DOWNLOAD = gql`
  query GetBalancingMechanismTrades(
    $filter: BalancingMechanismTradeFilter
    $paging: CursorPaging
    $siteId: ID!
  ) {
    site(id: $siteId) {
      balancingMechanismTrades(filter: $filter, paging: $paging) {
        edges {
          node {
            id
            start
            end
            power
            market
            side
            price
            tradeGroupSourceId
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
`;

const ALL_MARKETS_SUMMARY_PARTS = gql`
  fragment AllMarketsSummaryParts on AllMarketsSummaryDTO {
    revenue
    averagePoundPerMW
    revenueBreakdown {
      dcLow
      dcHigh
      drLow
      drHigh
      dmLow
      dmHigh
      positiveBr
      negativeBr
      positiveQr
      negativeQr
      positiveSr
      negativeSr
      positiveQrUtilisation
      negativeQrUtilisation
      positiveSrUtilisation
      negativeSrUtilisation
      intraday
      dayAheadHalfHourly
      dayAheadHourly
      systemPrice
      balancingMechanismBid
      balancingMechanismOffer
    }
  }
`;

const ANCILLARY_MARKET_SUMMARY_PARTS = gql`
  fragment AncillaryMarketSummaryParts on AncillaryTradeSummaryDTO {
    revenue
    averagePoundPerMW
    revenueBreakdown {
      dcLow
      dcHigh
      drLow
      drHigh
      dmLow
      dmHigh
      positiveBr
      negativeBr
      positiveQr
      negativeQr
      positiveSr
      negativeSr
      positiveQrUtilisation
      negativeQrUtilisation
      positiveSrUtilisation
      negativeSrUtilisation
    }
  }
`;

const WHOLESALE_MARKET_SUMMARY_PARTS = gql`
  fragment WholesaleMarketSummaryParts on WholesaleTradeSummaryDTO {
    revenue
    averagePoundPerMW
    revenueBreakdown {
      intraday
      dayAheadHalfHourly
      dayAheadHourly
      longTermHedge
      otc
      systemPrice
    }
  }
`;

const BALANCING_MECHANISM_MARKET_SUMMARY_PARTS = gql`
  fragment BalancingMechanismSummaryParts on BalancingMechanismSummaryDTO {
    revenue
    averagePoundPerMW
    revenueBreakdown {
      balancingMechanismBid
      balancingMechanismOffer
    }
  }
`;

const SITE_TRADE_SUMMARY_PARTS = gql`
  ${ALL_MARKETS_SUMMARY_PARTS}
  ${ANCILLARY_MARKET_SUMMARY_PARTS}
  ${WHOLESALE_MARKET_SUMMARY_PARTS}
  ${BALANCING_MECHANISM_MARKET_SUMMARY_PARTS}
  fragment SiteTradeSummaryParts on Site {
    id
    tradeSummary(start: $start, end: $end) {
      allMarkets {
        ...AllMarketsSummaryParts
      }
      ancillary {
        ...AncillaryMarketSummaryParts
      }
      wholesale {
        ...WholesaleMarketSummaryParts
      }
      balancingMechanism {
        ...BalancingMechanismSummaryParts
      }
    }
  }
`;

export const GET_SITE_TRADE_SUMMARY = gql`
  ${SITE_TRADE_SUMMARY_PARTS}
  query getSiteTradeSummary($siteId: ID!, $start: DateTime, $end: DateTime) {
    site(id: $siteId) {
      ...SiteTradeSummaryParts
    }
  }
`;

export const GET_DAILY_REPORT_SUBSCRIBED: TypedDocumentNode<
  { isDailyReportSubscribed: boolean },
  { siteId: number; userId: number }
> = gql`
  query IsDailyReportSubscribed($siteId: Int!, $userId: Int!) {
    isDailyReportSubscribed(siteId: $siteId, userId: $userId)
  }
`;

export const GET_SITE_TRADE_COUNT = gql`
  query TradeCount($siteId: ID!, $start: DateTime, $end: DateTime) {
    site(id: $siteId) {
      tradeCount(start: $start, end: $end) {
        ancillary {
          date
          count
        }
        wholesale {
          date
          count
        }
        balancingMechanism {
          date
          count
        }
        allMarkets {
          count
          date
        }
      }
    }
  }
`;

export const SUBSCRIBE_TO_UPDATED_TRADE = gql`
  subscription updatedTrade {
    updatedTrade {
      siteId
      tradeSourceKey
    }
  }
`;
